import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { recoilLoginState } from '../../RecoilStore';
import { useDropzone } from 'react-dropzone';
import imageCompression from "browser-image-compression";
import { format } from "date-fns";
import Loading from '../../components/Loading';
import { Icon, PageHead } from '../AdminLayout';

interface GalleryItem {
  id: number;
  image: string;
  subtitle: string;
  date: string;
}

type GalleryTab = 'program' | 'news' | 'support';

// 탭별 라벨 / API 경로 (조회: get{key}, 추가·수정·삭제: {key}/add 등)
const galleryTabs: { [key in GalleryTab]: { label: string; key: string } } = {
  program: { label: '프로그램', key: 'galleryprogram' },
  news: { label: '기관소식지', key: 'gallerynews' },
  support: { label: '후원현황', key: 'gallerysupport' },
};

export default function EditGallery() {
  const navigate = useNavigate();
  const isLogin = useRecoilValue(recoilLoginState);
  const [currentTab, setCurrentTab] = useState<GalleryTab>('program');
  const apiKey = galleryTabs[currentTab].key;
  const [images, setImages] = useState<GalleryItem[]>([]);
  // 개별 이미지 수정 상태
  const [editingImageIndex, setEditingImageIndex] = useState<number>(-1);
  const [editingImage, setEditingImage] = useState<GalleryItem | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(-1);
  const [itemImageFiles, setItemImageFiles] = useState<{ [key: number]: File[] }>({});
  
  // 이미지 첨부 관련 상태
  const [imageLoading, setImageLoading] = useState<boolean>(false);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  
  // 이미지 첨부 함수
  const currentDate = new Date();
  const date = format(currentDate, 'yyyy-MM-dd');
  
  // 임의의 영문+숫자 문자열 생성 함수
  const generateRandomString = (length: number): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // 이미지 압축 및 파일명 생성 함수
  const processImageFiles = async (acceptedFiles: File[]) => {
    try {
      // 허용 확장자 필터
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      const validFiles = acceptedFiles.filter(f => allowedTypes.includes(f.type));
      if (validFiles.length === 0) {
        alert('jpg, jpeg, png 형식의 이미지 파일만 업로드할 수 있습니다.');
        return [];
      }

      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1000
      };
      const resizedFiles = await Promise.all(
        validFiles.map(async (file) => {
          setImageLoading(true);
          const resizingBlob = await imageCompression(file, options);
          return resizingBlob;
        })
      );
      
      const adminId = 'admin'; // 관리자 ID
      const fileCopies = resizedFiles.map((resizedFile, index) => {
        const randomString = generateRandomString(10);
        // 확장자는 MIME 타입으로 결정하고, 원본 파일명은 사용하지 않음
        const originalFile = validFiles[index];
        const mime = originalFile.type || '';
        const extension = mime.includes('png')
          ? '.png'
          : mime.includes('jpeg') || mime.includes('jpg')
            ? '.jpg'
            : mime.includes('gif')
              ? '.gif'
              : '.jpg';
        
        return new File([resizedFile], `${date}${adminId}_${randomString}${extension}`, {
          type: acceptedFiles[index].type,
        });
      });
      setImageLoading(false);
      return fileCopies;
    } catch (error) {
      console.error('이미지 리사이징 중 오류 발생:', error);
      setImageLoading(false);
      return [];
    }
  };
  
  // 드롭존 설정
  const imageDropzone = useDropzone({ 
    onDrop: useCallback(async (acceptedFiles: File[]) => {
      const processedFiles = await processImageFiles(acceptedFiles);
      setImageFiles(processedFiles);
    }, []) 
  });

  // 개별 항목용 드롭존
  const itemDropzone = useDropzone({
    onDrop: useCallback(async (acceptedFiles: File[]) => {
      const processedFiles = await processImageFiles(acceptedFiles);
      const targetIndex = currentImageIndex >= 0 ? currentImageIndex : 0;
      setItemImageFiles(prev => ({ ...prev, [targetIndex]: processedFiles }));
      setCurrentImageIndex(-1);
    }, [currentImageIndex])
  });
  
  // 첨부 이미지 삭제 함수
  const deleteImage = (idx: number) => {
    const copy = [...imageFiles];
    const newItems = copy.filter((item, i) => i !== idx);
    setImageFiles(newItems);
  };

  const load = async () => {
    try {
      const res = await axios.get(`${MainURL}/main/get${apiKey}`);
      if (res.data && Array.isArray(res.data)) {
        setImages(res.data);
      } else {
        setImages([]);
      }
    } catch (error) {
      console.error('갤러리 데이터 로드 오류:', error);
      setImages([]);
    }
  };

  useEffect(() => {
    if (!isLogin) {
      alert('로그인이 필요합니다.');
      navigate('/admin');
      return;
    }
    load();
  }, [currentTab]);

  const onAddImage = async (file: File): Promise<string | null> => {
    try {
      console.log('이미지 업로드 시작:', file.name, file.size, file.type);
      const form = new FormData();
      form.append('img', file);
      
      console.log('FormData 생성됨, 업로드 URL:', `${MainURL}/main/upload/gallery`);
      
      const res = await axios.post(`${MainURL}/main/upload/gallery`, form, { 
        headers: { 'Content-Type': 'multipart/form-data' } 
      });
      
      console.log('서버 응답:', res.data);
      
      if (res.data && res.data.filenames && res.data.filenames.length > 0) {
        const filename = res.data.filenames[0];
        console.log('업로드 성공, 파일명:', filename);
        return filename;
      } else {
        console.error('서버 응답에 filename이 없음:', res.data);
        return null;
      }
    } catch (error: any) {
      console.error('이미지 업로드 실패:', error);
      if (error.response) {
        console.error('서버 응답 오류:', error.response.data);
        console.error('HTTP 상태:', error.response.status);
      }
      return null;
    }
  };

  const onReplaceImage = async (file: File): Promise<string | null> => {
    // 갤러리 업로드와 동일 경로 사용
    return onAddImage(file);
  };

  const moveImage = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= images.length) return;
    const copy = [...images];
    const [moved] = copy.splice(fromIndex, 1);
    copy.splice(toIndex, 0, moved);
    setImages(copy);
    // 순서 변경은 클라이언트에서만 표시하고, 실제 저장은 각 항목 수정 시 반영됨
  };

  const startEditImage = (idx: number) => {
    setEditingImageIndex(idx);
    setEditingImage({ ...images[idx] });
    setItemImageFiles({ [idx]: [] });
  };

  const cancelEditImage = () => {
    setEditingImageIndex(-1);
    setEditingImage(null);
    setItemImageFiles({});
  };

  const saveImageItem = async (idx: number) => {
    if (editingImage == null || !images[idx]) return;
    try {
      let imageFilename = editingImage.image;
      if (itemImageFiles[idx] && itemImageFiles[idx].length > 0) {
        const uploaded = await onReplaceImage(itemImageFiles[idx][0]);
        if (uploaded) imageFilename = uploaded;
      }

      const endpoint = `${MainURL}/main/${apiKey}/update`;

      const payload = {
        id: images[idx].id,
        image: imageFilename,
        subtitle: editingImage.subtitle || '',
        date: editingImage.date || ''
      };
      const res = await axios.post(endpoint, payload);
      if (res.data === true) {
        const updated = images.map((it, i) => 
          i === idx 
            ? { ...it, image: imageFilename, subtitle: editingImage.subtitle || '', date: editingImage.date || '' }
            : it
        );
        setImages(updated);
        setEditingImageIndex(-1);
        setEditingImage(null);
        setItemImageFiles({});
        alert('이미지 항목이 저장되었습니다.');
        load(); // 목록 새로고침
      } else {
        alert('저장에 실패했습니다.');
      }
    } catch (e) {
      console.error('갤러리 항목 저장 오류:', e);
      alert('저장 중 오류가 발생했습니다.');
    }
  };

  const deleteImageItem = async (idx: number) => {
    if (!images[idx] || !window.confirm('이 이미지를 삭제하시겠습니까?')) return;
    try {
      const endpoint = `${MainURL}/main/${apiKey}/delete`;

      const res = await axios.post(endpoint, { id: images[idx].id });
      if (res.data === true) {
        const copy = images.filter((_, i) => i !== idx);
        setImages(copy);
        alert('이미지가 삭제되었습니다.');
        load(); // 목록 새로고침
      } else {
        alert('삭제에 실패했습니다.');
      }
    } catch (e) {
      console.error('갤러리 항목 삭제 오류:', e);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  const addNewImage = async () => {
    if (imageFiles.length === 0) return;
    const filename = await onAddImage(imageFiles[0]);
    if (filename) {
      const endpoint = `${MainURL}/main/${apiKey}/add`;

      const res = await axios.post(endpoint, {
        image: filename,
        subtitle: '',
        date: ''
      });

      if (res.data && res.data.success) {
        setImageFiles([]);
        alert('이미지가 추가되었습니다.');
        load(); // 목록 새로고침
      } else {
        alert('이미지 추가에 실패했습니다.');
      }
    } else {
      alert('이미지 업로드에 실패했습니다.');
    }
  };

  const tabLabel = galleryTabs[currentTab].label;

  return (
    <>
      <PageHead title="갤러리 관리" desc="프로그램 · 기관소식지 · 후원현황 사진을 등록하고 부제목 · 날짜를 관리합니다." />

      <div className="ad-tabs" role="tablist">
        {(Object.keys(galleryTabs) as GalleryTab[]).map((tab) => (
          <button type="button" role="tab" key={tab} className={`ad-tab ${currentTab === tab ? 'is-on' : ''}`} onClick={() => setCurrentTab(tab)}>
            {galleryTabs[tab].label}
          </button>
        ))}
      </div>

      {/* 새 사진 등록 */}
      <div className="ad-card">
        <div className="ad-card__head">
          <h2>사진 등록</h2>
          <p>jpg · png 형식, {tabLabel} 갤러리에 추가됩니다.</p>
        </div>
        <div className="ad-card__pad">
          {imageLoading ? (
            <div className="ad-loading"><Loading/></div>
          ) : (
            <div className="ad-upload">
              <div {...imageDropzone.getRootProps()}>
                <input {...imageDropzone.getInputProps()} />
                <span className="ad-drop"><Icon name="image" />{imageFiles.length > 0 ? '다시 첨부하기' : '이미지 첨부하기'}</span>
              </div>
              {imageFiles.length > 0 && (
                <button type="button" className="ad-btn ad-btn--primary" onClick={addNewImage}>
                  <Icon name="plus" />
                  이미지 추가하기
                </button>
              )}
            </div>
          )}
          {imageFiles.length > 0 && (
            <div className="ad-files">
              {imageFiles.map((item: File, index: number) => (
                <div key={index} className="ad-file">
                  <img src={URL.createObjectURL(item)} alt="" />
                  <span>{item.name}</span>
                  <button type="button" className="ad-btn ad-btn--ghost ad-btn--icon is-danger" aria-label="첨부 취소" onClick={() => deleteImage(index)}>
                    <Icon name="close" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 목록 */}
      <div className="ad-card">
        <div className="ad-card__head">
          <h2>{tabLabel} 갤러리</h2>
          <p>{images.length}장</p>
        </div>
        <div className="ad-card__pad">
          {images.length === 0 ? (
            <div className="ad-empty">
              <Icon name="gallery" />
              <p>등록된 사진이 없습니다.</p>
            </div>
          ) : (
            <div className="ad-items">
              {images.map((img, idx) => (
                <div key={img.id || idx} className={`ad-item ${editingImageIndex === idx ? 'is-editing' : ''}`}>
                  <div className="ad-item__row">
                    <div className="ad-order">
                      <button type="button" aria-label="위로" disabled={idx === 0} onClick={() => moveImage(idx, idx - 1)}><Icon name="up" /></button>
                      <button type="button" aria-label="아래로" disabled={idx === images.length - 1} onClick={() => moveImage(idx, idx + 1)}><Icon name="down" /></button>
                    </div>
                    {img.image
                      ? <img className="ad-thumb" src={`${MainURL}/images/gallery/${img.image}`} alt="" />
                      : <span className="ad-thumb ad-thumb--empty"><Icon name="image" /></span>}
                    <div className="ad-item__body">
                      <h3>{img.subtitle || '부제목 없음'}</h3>
                      <p>{img.date || '날짜 없음'}</p>
                    </div>
                    <div className="ad-item__acts">
                      <button type="button" className="ad-btn ad-btn--sm" onClick={() => startEditImage(idx)}>
                        <Icon name="edit" />수정
                      </button>
                      <button type="button" className="ad-btn ad-btn--ghost ad-btn--sm is-danger" onClick={() => deleteImageItem(idx)}>
                        <Icon name="trash" />삭제
                      </button>
                    </div>
                  </div>

                  {/* 수정 모드 */}
                  {editingImageIndex === idx && editingImage && (
                    <div className="ad-item__edit">
                      <div className="ad-grid2">
                        <div className="ad-field">
                          <label>부제목</label>
                          <input placeholder='부제목' className='ad-input' value={editingImage.subtitle || ''} onChange={(e)=> setEditingImage({ ...editingImage, subtitle: e.target.value })} />
                        </div>
                        <div className="ad-field">
                          <label>날짜</label>
                          <input placeholder='예: 2026-09-29' className='ad-input' value={editingImage.date || ''} onChange={(e)=> setEditingImage({ ...editingImage, date: e.target.value })} />
                        </div>
                      </div>

                      <div className="ad-field">
                        <label>이미지</label>
                        <div className="ad-upload">
                          {editingImage.image
                            ? <img className="ad-thumb ad-thumb--md" src={`${MainURL}/images/gallery/${editingImage.image}`} alt="" />
                            : <span className="ad-thumb ad-thumb--md ad-thumb--empty"><Icon name="image" /></span>}
                          <div style={{ display:'grid', gap: 8, justifyItems: 'start' }}>
                            {imageLoading ? (
                              <div className="ad-loading"><Loading/></div>
                            ) : (
                              <div onClick={() => setCurrentImageIndex(idx)}>
                                <div {...itemDropzone.getRootProps()}>
                                  <input {...itemDropzone.getInputProps()} />
                                  <span className="ad-drop"><Icon name="image" />{(itemImageFiles[idx] || []).length > 0 ? '다시 첨부하기' : '새 이미지 첨부'}</span>
                                </div>
                              </div>
                            )}
                            {editingImage.image && (
                              <button type="button" className="ad-btn ad-btn--ghost ad-btn--sm is-danger"
                                onClick={() => {
                                  if (window.confirm('이미지 파일을 삭제하시겠습니까?')) {
                                    setEditingImage({ ...editingImage, image: '' });
                                    alert('이미지가 제거되었습니다. 저장하면 반영됩니다.');
                                  }
                                }}>
                                기존 이미지 제거
                              </button>
                            )}
                          </div>
                        </div>
                        {(itemImageFiles[idx] || []).length > 0 && (
                          <div className="ad-files">
                            {itemImageFiles[idx].map((file: File, fIdx: number) => (
                              <div key={fIdx} className="ad-file">
                                <img src={URL.createObjectURL(file)} alt="" />
                                <span>{file.name}</span>
                                <button type="button" className="ad-btn ad-btn--ghost ad-btn--icon is-danger" aria-label="첨부 취소"
                                  onClick={() => {
                                    setItemImageFiles(prev => {
                                      const current = prev[idx] || [];
                                      const filtered = current.filter((_, i) => i !== fIdx);
                                      return { ...prev, [idx]: filtered };
                                    });
                                  }}>
                                  <Icon name="close" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="ad-item__editfoot">
                        <button type="button" className="ad-btn" onClick={cancelEditImage}>취소</button>
                        <button type="button" className="ad-btn ad-btn--primary" onClick={() => saveImageItem(idx)}>저장</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {images.length > 1 && (
            <p className="ad-hint" style={{ marginTop: 14 }}>위 · 아래 화살표로 바꾼 순서는 현재 화면에만 적용되며 서버에 저장되지 않습니다.</p>
          )}
        </div>
      </div>
    </>
  );
}
